import { useEffect, useState } from 'react'
import accountingService from '@api/v1/accountingService'

/**
 * CC-49837: account click-through for the financial statements.
 *
 * BS/GL rows come from a server-parsed CSV, so they carry only display
 * strings — no account ids. This hook loads the Chart of Accounts once and
 * indexes it by (normalized) name and by account number so report cells can
 * be linkified to the account register. Trial Balance rows carry a real
 * `accountId` and can link directly without the index.
 *
 * Links are a progressive enhancement: any label that doesn't resolve stays
 * plain text, and a failed accounts fetch simply renders no links.
 */
const norm = (s) => String(s ?? '').trim().toLowerCase()

// CC-50018-batch regression fix (CC-49837 round 3): account numbers are NOT
// unique. vladdemo alone carries three duplicate-number pairs (#10101 "BOK
// Operating" 161 / "BOK Operating 2" 162, #6025, #6000.002.004.003), and the
// round-2 resolver matched the NUMBER first with a last-write-wins map — so
// clicking "BOK Operating" ($119,083.00) opened "BOK Operating 2" ($0.00) on a
// financial statement. A key claimed by two different accounts now resolves to
// nothing (plain text, per the progressive-enhancement rule below) instead of
// to whichever account happened to load last.
const AMBIGUOUS = Symbol('duplicate-account-key')

const setUnique = (map, key, id) => {
  if (!map.has(key)) map.set(key, id)
  else if (map.get(key) !== id) map.set(key, AMBIGUOUS)
}

const resolve = (map, key) => {
  const v = map.get(key)
  return v == null || v === AMBIGUOUS ? null : v
}

// Exported so tests can build the index from a REAL Chart-of-Accounts payload
// (duplicate numbers included) instead of hand-crafting maps that can't
// contain the collision this fix exists for.
export const buildAccountsIndex = (accounts) => {
  const byName = new Map()
  const byNumber = new Map()
  for (const a of accounts || []) {
    if (a?.id == null) continue
    if (a.name) setUnique(byName, norm(a.name), a.id)
    if (a.accountNumber) setUnique(byNumber, String(a.accountNumber).trim(), a.id)
  }
  return { byName, byNumber }
}

export const useAccountsIndex = () => {
  const [index, setIndex] = useState(null)
  useEffect(() => {
    let stale = false
    accountingService
      .getAccounts('0')
      .then((res) => {
        if (stale) return
        setIndex(buildAccountsIndex(res?.data))
      })
      .catch(() => { if (!stale) setIndex(null) })
    return () => { stale = true }
  }, [])
  return index
}

// Resolve a CSV account label to an account id — or null, never a guess.
// Labels arrive in a few shapes: "Operating Account", "1000 - Operating
// Account", "1000 Operating". The NAME outranks the number: names distinguish
// the duplicate-number pairs above, while two same-named accounts with
// different numbers still resolve through the number fallback. Only a label
// whose name AND number are both duplicated stays unlinked — that residual
// needs CB to emit a real account id in the report payload (it carries only
// display strings today).
export const accountIdFor = (index, label) => {
  if (!index) return null
  const raw = String(label ?? '').trim()
  if (!raw) return null
  const direct = resolve(index.byName, norm(raw))
  if (direct != null) return direct
  // "number - name" / "number name": prefer the remainder as a name, then the
  // leading token as an account number.
  const m = raw.match(/^([\d.]+)\s*[-–·]?\s*(.*)$/)
  if (m) {
    if (m[2]) {
      const byRest = resolve(index.byName, norm(m[2]))
      if (byRest != null) return byRest
    }
    const byNum = resolve(index.byNumber, m[1])
    if (byNum != null) return byNum
  }
  return null
}

export const registerHref = (accountId) => `/accounting?account=${encodeURIComponent(accountId)}`

// Wrap an ALREADY-ESCAPED label in a register link when it resolves.
// data-cb-nav marks the anchor for the SPA click interceptor in
// SpecializedReportShell (plain href keeps middle-click/new-tab working).
export const accountLinkHtml = (index, rawLabel, escapedLabel) => {
  const id = accountIdFor(index, rawLabel)
  if (id == null) return escapedLabel
  return `<a href="${registerHref(id)}" data-cb-nav class="text-cb-accent-text hover:underline">${escapedLabel}</a>`
}
